"use client"

/*
 * DEV HANDOFF - settings-page.tsx
 *
 * The shell both settings screens wear: back link and header actions, the
 * scope note, the tab strip, the divided settings list, the save/cancel footer,
 * and the delete card. `feed-settings.tsx` (an automation) and
 * `social-settings.tsx` (a destination) are the same screen over different
 * data, so they are one shell over two specs rather than two copies that drift.
 *
 * Pairs with `settings-form.tsx`, which renders the rows.
 *
 * WIRING: everything here is presentational. `activeSection` is controlled,
 * `dirty` / `saving` / `errorCounts` / `formError` come from the container's
 * existing deep-equal diff and ErrorModule plumbing, every control is inert.
 */

import * as React from "react"
import { ChevronLeft, Info, TriangleAlert, X } from "lucide-react"

import { META_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import type { NetworkSlug } from "@/lib/networks"
import { cn } from "@/lib/utils"
import {
  SettingsFields,
  SettingsFieldsSkeleton,
  SettingsFormProvider,
  type SettingsChange,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"
import {
  CARD,
  CARD_DESCRIPTION,
  CARD_TITLE,
  SM_BUTTON,
} from "@/shadcn/components/styles"
import {
  Alert,
  AlertAction,
  AlertDescription,
} from "@/shadcn/components/ui/alert"
import { Button } from "@/shadcn/components/ui/button"
import { Card } from "@/shadcn/components/ui/card"
import { NetworkBadge } from "@/shadcn/components/ui/networks"
import { Skeleton } from "@/shadcn/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/shadcn/components/ui/tabs"

/** Muted icon-and-label action, matching automations.tsx's panel buttons. */
export const GHOST =
  "tw:text-muted-foreground tw:hover:text-foreground tw:focus-visible:text-foreground"

/** One header action: an icon and a label, e.g. History or Medic. */
export type HeaderAction = {
  label: string
  icon: React.ReactNode
  /**
   * Where it goes. Optional because a preview state has nowhere to send the
   * reader, but a mounted screen without one renders a button that does
   * nothing, which is worse than no button.
   */
  onClick?: () => void
}

/* ───────────────────────────────────────────────────────────────── parts ── */


/**
 * The header's avatar: the account's picture, or the feed's icon, with its
 * network's badge on the corner - the legacy `SocialAvatar` drawing
 * (`social.icon`, else `social.src`; `input.icon` for a source) and the one
 * Create Automation's destination tiles use.
 *
 * Falls back to initials when there is no picture or it fails to load: these
 * URLs point at a network's CDN or a publisher's site, so a dead one is
 * routine, and a broken-image glyph in the page title is worse than initials.
 *
 * 30px with a 14px badge (Emily, 2026-10-01: 25% down from 40 / 18), keeping
 * the Create Automation tile's proportions, the badge at about 46% of the
 * avatar. `size-7.5` is 30px on the spacing scale.
 */
export function SettingsAvatar({
  name,
  avatarUrl,
  network,
}: {
  name: string
  avatarUrl?: string
  /** `null` draws no badge, for a network with no artwork. */
  network?: NetworkSlug | null
}) {
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => setFailed(false), [avatarUrl])
  const showImage = Boolean(avatarUrl) && !failed

  return (
    <span className="tw:relative tw:inline-block tw:size-7.5 tw:shrink-0">
      <span
        className={cn(
          "tw:flex tw:size-full tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:text-xs tw:font-medium tw:text-muted-foreground",
          !showImage && "tw:bg-muted"
        )}
      >
        {showImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarUrl}
            alt=""
            className="tw:size-full tw:object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          initialsOf(name)
        )}
      </span>
      {network ? (
        /* The brand disc with a white glyph, as legacy's service icon and
           Create Automation's tiles draw it; its own 2px ring of the page
           keeps it readable against any photo. Decorative: the meta line
           under the title names the network. */
        <NetworkBadge
          network={network}
          size={14}
          decorative
          className="tw:absolute tw:-right-0.5 tw:-bottom-0.5"
        />
      ) : null}
    </span>
  )
}

/** Up to two initials, as Create Automation's `initialsFromName` makes them. */
function initialsOf(name: string): string {
  return name
    .replace(/^@/, "")
    .split(/[\s._·-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
}
function PageHeader({
  title,
  icon,
  meta,
  backLabel,
  onBack,
  actions,
  note,
  onDismissNote,
  loading,
}: {
  title: string
  icon?: React.ReactNode
  meta?: string
  /** Omit for a surface with no parent to go back to, e.g. inside a dialog. */
  backLabel?: string
  onBack?: () => void
  actions?: HeaderAction[]
  note?: string | null
  onDismissNote?: () => void
  loading?: boolean
}) {
  return (
    <header className="tw:flex tw:w-full tw:flex-col tw:gap-4">
      {backLabel || actions?.length ? (
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        {backLabel ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(GHOST, "tw:-ml-2.5")}
            onClick={onBack}
          >
            <ChevronLeft />
            {backLabel}
          </Button>
        ) : (
          <span />
        )}
        {actions?.length ? (
          <div className="tw:-mr-2.5 tw:flex tw:items-center tw:gap-1">
            {actions.map((action) => (
              <Button
                key={action.label}
                type="button"
                variant="ghost"
                size="sm"
                className={GHOST}
                onClick={action.onClick}
              >
                {action.icon}
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      ) : null}
      <div className="tw:flex tw:flex-col tw:gap-1">
        {loading ? (
          <>
            <Skeleton className="tw:h-8 tw:w-64" />
            <Skeleton className="tw:h-5 tw:w-40" />
          </>
        ) : (
          <>
            {/* The mark sits beside the name only, centred on its line, so
                the meta line under it keeps its left edge. `min-w-0` lets a
                long name wrap instead of pushing the row wide. */}
            <div className="tw:flex tw:items-center tw:gap-3">
              {icon}
              <h1 className="tw:min-w-0 tw:text-2xl tw:font-semibold tw:text-foreground">
                {title}
              </h1>
            </div>
            {meta ? <p className={cn(META_TEXT, "tw:text-sm")}>{meta}</p> : null}
          </>
        )}
      </div>
      {/*
        * The `Alert` primitive rather than a hand-rolled muted box: the same
        * informational band automations.tsx renders in its expanded panel.
        *
        * DISMISSIBLE. This is one-time orientation, not a standing warning, and
        * it rendered on all seven tabs on every visit - 56px above the fold,
        * two lines on a phone. `onDismissNote` follows the convention
        * messages.tsx sets for its banners: the component exposes the handler
        * and PROD OWNS PERSISTENCE (server-side, or localStorage). Nothing here
        * remembers the dismissal, so the kit stays drop-in and the preview can
        * show both states.
        *
        * To suppress the note entirely pass `note={null}` - which is also what
        * a dismissal passes. There is deliberately no "permanent note" flag:
        * both notes on both screens are orientation, so a flag would exist for
        * a case that does not.
        */}
      {note ? (
        /*
         * MOBILE OVERRIDE: `pr-18`. The primitive reserves 72px on the right
         * as soon as an `AlertAction` is present, sized for a text button
         * ("Undo", "View"). Ours is a 24px `icon-xs` X at `right-2`, so it
         * needs 32. At 375px that handed 40px of a 295px alert to nothing and
         * squeezed the text into a 187px column. Overridden WITH THE SAME
         * `has-data-` variant, because a bare `tw:pr-9` loses to it on
         * specificity.
         *
         * `AlertDescription`'s `text-balance` below `md` LOOKS like a second
         * bug here and is not - measured, not assumed. At this column width
         * balance and pretty lay the text out identically (lines 195/203px
         * both ways); the 3-lines-to-2 gain came entirely from the padding
         * above. Balance only diverges in the 187px column the `pr-18` bug
         * created, and there it is the BETTER of the two (three even lines
         * 138/131/126 against pretty's 154/162/78 with an orphan tail). So
         * `alert.tsx` is left verbatim and nothing is overridden here.
         */
        <Alert className="tw:has-data-[slot=alert-action]:pr-9">
          <Info />
          <AlertDescription>{note}</AlertDescription>
          {/* Rendered whenever there IS a note, inert until prod attaches a
              handler - the same way every other control on these screens shows
              its affordance and does nothing. Gating it on `onDismissNote`
              meant the X only appeared in the one host-backed preview state,
              so the affordance was invisible in review. */}
          <AlertAction>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss"
              className={GHOST}
              onClick={onDismissNote}
            >
              <X />
            </Button>
          </AlertAction>
        </Alert>
      ) : null}
    </header>
  )
}

/** The red count legacy puts on a tab whose fields failed validation. */
function ErrorDot({ count }: { count: number }) {
  return (
    <span
      aria-label={`${count} ${count === 1 ? "problem" : "problems"}`}
      className="tw:ml-1 tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-destructive tw:text-[10px] tw:font-semibold tw:text-destructive-foreground"
    >
      {count}
    </span>
  )
}

/**
 * "This tab has unsaved edits." Seven tabs share one record and one Save, so
 * without this you can edit Hashtags, move to Filters, and have nothing tell
 * you the first tab is still dirty.
 *
 * Deliberately smaller and wordless next to `ErrorDot`: an error is a thing to
 * go and read, an edit is a thing to remember. Primary rather than a neutral
 * grey because it is the same state the primary Save button is reporting, and
 * tying the two colours makes the dot legible as "there is something to save".
 */
function DirtyDot() {
  return (
    <span
      aria-label="unsaved changes"
      className="tw:ml-1.5 tw:size-1.5 tw:shrink-0 tw:rounded-full tw:bg-primary"
    />
  )
}

export function FormFooter({
  dirty,
  saving,
  onSave,
  onCancel,
}: {
  dirty?: boolean
  saving?: boolean
  onSave?: () => void
  onCancel?: () => void
}) {
  return (
    <div className="tw:flex tw:w-full tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:items-center tw:sm:justify-end">
      {/* outline, not ghost: every other Cancel in the app is outline -
          stepper-card.tsx, prod's account/AvatarDialog.tsx, and
          connect-social.tsx (whose backVariant defaults to it). This footer was
          the only ghost one. */}
      <Button
        type="button"
        variant="outline"
        className={TAP_TARGET}
        disabled={saving}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="button"
        className={TAP_TARGET}
        loading={saving}
        disabled={!dirty || saving}
        onClick={onSave}
      >
        {saving ? "Saving..." : "Save changes"}
      </Button>
    </div>
  )
}

/**
 * The delete card, matching production's `account/DeleteAccountCard.tsx`
 * exactly: plain `CARD`, one padded row, title over description, a
 * `variant="destructive"` button (already the tinted `bg-destructive/10` style,
 * not a solid red fill) at `size="sm"` + `SM_BUTTON`.
 *
 * The Figma automation-settings frame (29887:1442) draws a `base/destructive`
 * stroke and a tinted header strip instead. That predates the account page; the
 * account treatment wins and the frame should be redrawn.
 */
export function SettingsDeleteCard({
  title,
  description,
  buttonLabel,
  onDelete,
}: {
  title: string
  description: string
  buttonLabel: string
  onDelete?: () => void
}) {
  return (
    <Card className={CARD}>
      {/*
        * STACKS ON A PHONE. The button is `shrink-0` and the text column was
        * `flex-1 min-w-0`, so at 375px the ~150px button held its width and
        * the text got ~120px: the title broke across two lines and the
        * description ran to eight, in a column narrower than the button beside
        * it. `flex-wrap` was already here and could never fire, because
        * `flex-1` is `flex: 1 1 0%` and a row only wraps when an item cannot
        * fit its BASIS. So `basis-full` below `sm`, `sm:basis-0` from there.
        *
        * The button keeps its natural width and sits left, rather than going
        * full width like the Save / Cancel footer above: a destructive action
        * should not be the easiest thing on the page to hit with a thumb.
        *
        * NOTE FOR THE PORT: prod's `account/DeleteAccountCard.tsx` has this
        * same squeeze and not even the `flex-wrap` - this card was built to
        * match it. The fix belongs there too; see LEGACY-QUESTIONS A7.
        */}
      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-4 tw:p-4 tw:sm:gap-y-2">
        <div className="tw:flex tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:flex-col tw:gap-1 tw:sm:basis-0">
          <p className={CARD_TITLE}>{title}</p>
          <p className={CARD_DESCRIPTION}>{description}</p>
        </div>
        {/* size="sm" + SM_BUTTON because this button lives INSIDE a card, where
            the page-level footer above uses TAP_TARGET. Each matches its
            neighbours. */}
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className={cn(SM_BUTTON, "tw:shrink-0")}
          onClick={onDelete}
        >
          {buttonLabel}
        </Button>
      </div>
    </Card>
  )
}

/**
 * The middle of a settings screen: the form-level error, the `beforeTabs` slot,
 * the tab strip and the active section's fields, plus their loading skeletons.
 *
 * SEPARATE FROM `SettingsPage` BECAUSE THERE ARE TWO SURFACES. A page wraps
 * this in a heading and a footer (`SettingsPage`); a dialog wraps the same
 * thing in `DialogHeader` / `DialogFooter` (`destination-override.tsx`). The
 * alternative was a `surface` prop on `SettingsPage` switching its own chrome
 * on and off, which is the shape that already went wrong with `scope` - a flag
 * standing in for two genuinely different containers. This way the shared part
 * is shared by being one component, and neither surface can drift in how it
 * resolves the active tab or draws the dirty dots.
 */
export function SettingsBody({
  sections,
  activeSection,
  onSectionChange,
  dirtySections,
  errorCounts,
  formError,
  beforeTabs,
  loading,
  formKey,
}: Pick<
  SettingsPageProps,
  | "sections"
  | "activeSection"
  | "onSectionChange"
  | "dirtySections"
  | "errorCounts"
  | "formError"
  | "beforeTabs"
  | "loading"
  | "formKey"
>) {
  const first = sections[0]?.id ?? ""
  const current =
    activeSection && sections.some((s) => s.id === activeSection)
      ? activeSection
      : first
  const section = sections.find((s) => s.id === current) ?? sections[0]

  /* A single-tab destination (YouTube, Pinterest, TikTok) gets no strip: one
     tab is not a choice, and drawing it implies there are others. */
  const showTabs = sections.length > 1

  return (
    <>
      {formError ? (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      ) : null}

      {loading ? null : beforeTabs}

      <div className="tw:flex tw:w-full tw:flex-col tw:gap-6">
        {loading ? (
          /* The tab SET is not knowable either: legacy picks it off the
             network, so a real strip here would be a guess that reflows once
             the record lands. */
          <div className="tw:flex tw:w-full tw:gap-4 tw:border-b tw:border-border tw:pb-2">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="tw:h-5 tw:w-16" />
            ))}
          </div>
        ) : showTabs ? (
          /* TabsList is `inline-flex w-fit` and cannot shrink: seven triggers
             are well past a phone's width, so the strip would push the page
             sideways. Scroll it instead. `-my-1 py-1` keeps room for the
             triggers' focus ring, which overflow-x would otherwise clip. */
          <div className="tw:-my-1 tw:w-full tw:overflow-x-auto tw:py-1">
            <Tabs
              value={current}
              onValueChange={onSectionChange}
              className="tw:w-max tw:min-w-full"
            >
              <TabsList
                variant="line"
                className="tw:w-max tw:min-w-full tw:justify-start tw:rounded-none tw:border-b tw:border-border"
              >
                {sections.map((item) => (
                  <TabsTrigger
                    key={item.id}
                    value={item.id}
                    className="tw:flex-none"
                  >
                    {item.label}
                    {/* Errors outrank dirt: a tab that failed validation has
                        something to fix, which matters more than the fact it
                        was edited, and two marks on one trigger is noise. */}
                    {errorCounts?.[item.id] ? (
                      <ErrorDot count={errorCounts[item.id]} />
                    ) : dirtySections?.includes(item.id) ? (
                      <DirtyDot />
                    ) : null}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        ) : null}

        {loading ? (
          <SettingsFieldsSkeleton />
        ) : section ? (
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
            {/* The tab already names the section, so this says what it is
                FOR. One line of orientation costs nothing and stops each tab
                opening on an unexplained list of controls. */}
            {section.description ? (
              <p className="tw:text-sm tw:text-muted-foreground">
                {section.description}
              </p>
            ) : null}
            <SettingsFields fields={section.fields} formKey={formKey} />
          </div>
        ) : null}
      </div>
    </>
  )
}

/**
 * The delete confirmation - Figma `29890:1501`, a clone of the Create
 * Automation live-confirmation shell: 440 card, 28px gap (`gap-7`, the
 * canonical modal spacing per `claude-to-figma/CLAUDE.md` §3 and §9), centred
 * destructive badge, stacked full-width buttons with the primary first.
 *
 * Renders the dialog SURFACE only, like `new-social-suggest-feeds.tsx`. In prod
 * this is an alert dialog: drop it into `AlertDialogContent` and apply the
 * `.tw-root` portal patch there.
 */
export function SettingsDeleteDialog({
  className,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  deleting,
  deletingLabel = "Deleting...",
  onConfirm,
  onCancel,
  onClose,
}: {
  className?: string
  title: string
  description: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  deleting?: boolean
  deletingLabel?: string
  onConfirm?: () => void
  onCancel?: () => void
  onClose?: () => void
}) {
  return (
    <div
      className={cn(
        "tw:flex tw:w-full tw:max-w-[440px] tw:flex-col tw:gap-7 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-4 tw:font-sans tw:shadow-xs tw:md:p-6",
        className
      )}
    >
      <div className="tw:flex tw:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          className={GHOST}
          onClick={onClose}
        >
          <X />
        </Button>
      </div>

      <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:text-center">
        {/* The tinted circle is the badge treatment from the Figma frame, built
            from the destructive token at 10% rather than a literal colour. */}
        <div className="tw:flex tw:size-14 tw:items-center tw:justify-center tw:rounded-full tw:bg-destructive/10">
          <TriangleAlert className="tw:size-6 tw:text-destructive" />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h2 className="tw:text-xl tw:font-semibold tw:text-foreground">
            {title}
          </h2>
          <p className="tw:text-sm tw:text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Stacked full-width, primary first - the canonical footer. */}
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
        <Button
          type="button"
          variant="destructive"
          className={cn(TAP_TARGET, "tw:w-full")}
          loading={deleting}
          onClick={onConfirm}
        >
          {deleting ? deletingLabel : confirmLabel}
        </Button>
        {/*
          * OUTLINE, NOT GHOST (G2, Emily's call: consistency with the other
          * dialogs).
          *
          * This was `ghost`, which is faithful to the Figma frame
          * (`29890:1501` draws Cancel as borderless text). But
          * `cancel-account.tsx` uses `outline` for all SIX of its dialog
          * secondary buttons, and so do `stepper-card.tsx` and prod's
          * `account/AvatarDialog.tsx`. One had to give, and six shipped
          * dialogs beat one frame.
          *
          * The argument for ghost, recorded because it is not a bad one: the
          * destructive button above is a TINT rather than a fill, so an
          * outlined Cancel beneath it competes rather than recedes. If that
          * turns out to read badly in review, the fix is to reconsider the
          * destructive button's weight, not to make this dialog the only one
          * with a ghost Cancel.
          *
          * FIGMA NOW DISAGREES, so this joins D3's redraw list with D2 and D5.
          */}
        <Button
          type="button"
          variant="outline"
          className={cn(TAP_TARGET, "tw:w-full")}
          disabled={deleting}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────── the shell ── */

export type SettingsPageProps = {
  className?: string
  sections: SettingsSection[]

  title: string
  /**
   * Drawn beside the title: a `SettingsAvatar` on the pages. Omit where no
   * single source or account applies, as on a route holding several sources.
   */
  icon?: React.ReactNode
  meta?: string
  /** Omit for a surface with no parent to go back to, e.g. inside a dialog. */
  backLabel?: string
  /** What the back link does. Omit with `backLabel` on a dialog surface. */
  onBack?: () => void
  actions?: HeaderAction[]
  /**
   * The scope note above the tabs.
   *
   * `null` means explicitly none - which is what a dismissal passes. It cannot
   * be `undefined`, because the screens give `note` a destructuring default, so
   * undefined is refilled with the default text.
   */
  note?: string | null
  /**
   * Makes the note dismissible. Omit for a permanent note. Prod owns
   * persistence; nothing here remembers the dismissal.
   */
  onDismissNote?: () => void

  /** Controlled. Falls back to the first section. */
  activeSection?: string
  onSectionChange?: (id: string) => void

  /** The record has not arrived yet. Renders skeletons, hides save and delete. */
  loading?: boolean
  dirty?: boolean
  saving?: boolean
  /** Section id -> failed field count. Drives the tab's error count. */
  errorCounts?: Record<string, number>
  /**
   * Section ids with unsaved edits. Drives the tab's dirty dot, and `dirty`
   * falls back to this so the tabs and the Save button cannot disagree.
   */
  dirtySections?: string[]
  formError?: string

  /**
   * Rendered between the note and the tab strip. Carries the inherit switch on
   * a per-automation override; nothing uses it on a global screen.
   */
  beforeTabs?: React.ReactNode

  /*
   * OPTIONAL BECAUSE A SCREEN WITHOUT A DELETE CARD HAS NO DELETE COPY TO
   * GIVE. `destination-override.tsx` is a dialog surface and deletes nothing,
   * and requiring it to invent three strings it never renders would be the
   * type lying about what the screen is. The card renders only when all three
   * are present, so the copy and the card arrive together or not at all.
   */
  deleteTitle?: string
  deleteDescription?: string
  deleteLabel?: string
  /** Hide the delete card entirely, e.g. on a surface inside a dialog. */
  showDelete?: boolean

  /**
   * Every field-level edit, from any control on any tab. The host owns what a
   * change means — see `SettingsChange`.
   */
  onChange?: (change: SettingsChange) => void
  /**
   * Remounts the field list when it changes, so the uncontrolled text inputs
   * pick up a different record. Pass the record's id.
   */
  formKey?: string

  onSave?: () => void
  onCancel?: () => void
  onDelete?: () => void
}

export function SettingsPage({
  className,
  sections,
  title,
  icon,
  meta,
  backLabel,
  onBack,
  actions,
  note,
  onDismissNote,
  activeSection,
  onSectionChange,
  loading,
  dirty,
  saving,
  errorCounts,
  dirtySections,
  formError,
  beforeTabs,
  onChange,
  formKey,
  deleteTitle,
  deleteDescription,
  deleteLabel,
  showDelete = true,
  onSave,
  onCancel,
  onDelete,
}: SettingsPageProps) {
  /* Explicit `dirty` still wins, so a caller can show the footer live without
     naming sections; otherwise any dirty section makes the record dirty. */
  const isDirty = dirty ?? Boolean(dirtySections?.length)

  return (
    <SettingsFormProvider onChange={onChange}>
      <div
        className={cn(
          /* max-w-[968px] mx-auto, the same measure prod's AccountPage and the
             kit's subscription-page use. Left unbounded, the page stretches to
             the monitor and every toggle puts its switch a screen away from its
             label. "Full width of the content area" is this, not "no limit". */
          "tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:flex-col tw:gap-6 tw:p-4 tw:font-sans tw:text-foreground tw:md:p-6",
          className
        )}
      >
        <PageHeader
          title={title}
          icon={icon}
          meta={meta}
          backLabel={backLabel}
          onBack={onBack}
          actions={actions}
          note={loading ? undefined : note}
          onDismissNote={onDismissNote}
          loading={loading}
        />

        <SettingsBody
          sections={sections}
          activeSection={activeSection}
          onSectionChange={onSectionChange}
          dirtySections={dirtySections}
          errorCounts={errorCounts}
          formError={formError}
          beforeTabs={beforeTabs}
          loading={loading}
          formKey={formKey}
        />

        {/* Nothing to save and nothing to delete until the record is here. */}
        {loading ? null : (
          <>
            <FormFooter
              dirty={isDirty}
              saving={saving}
              onSave={onSave}
              onCancel={onCancel}
            />

            {/*
              * Extra top margin on purpose. The page stacks its blocks at
              * gap-6 (24px), which put Delete automation 61px below Save
              * changes and within 17px of it horizontally - the two most
              * consequential controls on the screen, in one visual column, one
              * of them destructive. Doubling the gap to 48px separates the
              * irreversible action from the routine one.
              */}
            {showDelete && deleteTitle && deleteDescription && deleteLabel ? (
              <div className="tw:mt-6 tw:w-full">
                <SettingsDeleteCard
                  title={deleteTitle}
                  description={deleteDescription}
                  buttonLabel={deleteLabel}
                  onDelete={onDelete}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </SettingsFormProvider>
  )
}
